import React, {
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { AuthContext } from "../../context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import "../../App.css";
import Loader from "../ui/Loader";
import Modal from "../ui/Modal";
import PaymentModal from "../Payment";
import { swalError, swalSuccess, swalConfirm } from "../../utils/swalAlert";
import { fetchWithAuth } from "../../utils/api";
import { usePushNotificationContext } from "../../context/PushNotificationContext";
import PWAInstallBanner from "./PWAInstallBanner";
import MaintenanceBanner from "./MaintenanceBanner";

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

export default function Navbar({
  links = [],
  createButtons = [],
  className = "",
  buttonClassName = "",
  breadcrumbs = [],
  isProfileHidden = false,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user, primaryRole, switchRole } = useContext(AuthContext);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isBreadcrumbsOpen, setIsBreadcrumbsOpen] = useState(false);
  const [apiKeyExists, setApiKeyExists] = useState(null);
  const [isApiKeyModalOpen, setIsApiKeyModalOpen] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [showApiKey, setShowApiKey] = useState(false);
  const [isApiKeyLoading, setIsApiKeyLoading] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const dropdownRef = useRef(null);

  const isUdghoshStudent =
    (user?.category === "UDGHOSH" || user?.institute?.category === "UDGHOSH") &&
    primaryRole === "student";

  const udghoshExpiryInfo = useMemo(() => {
    if (!isUdghoshStudent || !user?.loginExpiryDate) return null;
    const expDate = new Date(user.loginExpiryDate);
    const diffTime = expDate.getTime() - Date.now();
    const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return {
      daysRemaining,
      isExpired: daysRemaining <= 0,
      formattedDate: expDate.toLocaleDateString("en-US", {
        day: "numeric",
        month: "short",
        year: "numeric",
      }),
    };
  }, [isUdghoshStudent, user?.loginExpiryDate]);

  // Change Password state
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [pushMessage, setPushMessage] = useState(null);

  const {
    supported: pushSupported,
    permission: pushPermission,
    subscribed: pushSubscribed,
    loading: pushLoading,
    actionLoading: pushActionLoading,
    subscribe: pushSubscribe,
    unsubscribe: pushUnsubscribe,
  } = usePushNotificationContext();

  const handleNotificationClick = useCallback(() => {
    // Push notifications aren't supported by this browser/environment.
    // Keep the notification icon visible, but explain why it cannot be used.
    if (!pushSupported) {
      setPushMessage({
        type: "unsupported",
        title: "Notifications not supported",
        message:
          "Your current browser or environment does not support push notifications.",
      });
      return;
    }

    // The browser has permanently blocked notifications for this site.
    // Calling subscribe() again will not show the browser permission prompt.
    if (pushPermission === "denied") {
      setPushMessage({
        type: "blocked",
        title: "Notifications are blocked",
        message:
          "Your browser is currently blocking notifications for this site.",
      });
      return;
    }

    // Prevent multiple operations while subscribe/unsubscribe is running.
    if (pushActionLoading || pushLoading) return;

    if (pushSubscribed) {
      pushUnsubscribe();
    } else {
      pushSubscribe();
    }
  }, [
    pushSupported,
    pushPermission,
    pushActionLoading,
    pushLoading,
    pushSubscribed,
    pushSubscribe,
    pushUnsubscribe,
  ]);

  // Animation variants for different elements
  const navbarVariants = {
    hidden: { y: -100 },
    visible: {
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 20,
      },
    },
  };

  const handleBreadcrumbsToggle = () => {
    setIsBreadcrumbsOpen((prev) => !prev);
  };

  const handleProfileClick = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Fetch API key existence on dropdown open
  useEffect(() => {
    if (isDropdownOpen) {
      fetchApiKeyStatus();
    }
    // eslint-disable-next-line
  }, [isDropdownOpen]);

  const fetchApiKeyStatus = async () => {
    try {
      setIsApiKeyLoading(true);
      const res = await fetch(`${serverUrl}/api/api-key/my-keys`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch API key status");
      const data = await res.json();
      setApiKeyExists(Array.isArray(data) && data.length > 0);
    } catch (err) {
      setApiKeyExists(false);
    } finally {
      setIsApiKeyLoading(false);
    }
  };

  const extractKeyString = (data) => {
    if (typeof data === "string") {
      return data;
    }
    if (
      typeof data.apiKey === "object" &&
      data.apiKey !== null &&
      typeof data.apiKey.key === "string"
    ) {
      return data.apiKey.key;
    }
    if (typeof data.apiKey === "string") {
      return data.apiKey;
    }
    if (typeof data.key === "string") {
      return data.key;
    }
    return "";
  };

  const handleViewProfile = () => {
    navigate("/user-profile");
  };

  const handleGenerateApiKey = async () => {
    try {
      setIsApiKeyLoading(true);
      const res = await fetch(`${serverUrl}/api/api-key/generate`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to generate API key");
      const data = await res.json();
      const keyString = extractKeyString(data);
      setApiKey(keyString);
      setShowApiKey(false);
      setIsApiKeyModalOpen(true);
      setApiKeyExists(true);
    } catch (err) {
      swalError(err.message || "Failed to generate API key");
    } finally {
      setIsApiKeyLoading(false);
    }
  };

  const handleRegenerateApiKey = async () => {
    const result = await swalConfirm(
      "Regenerate API Key?",
      "Your old API key will be invalidated. Continue?",
      "Yes, Regenerate",
      "Cancel",
    );
    if (!result.isConfirmed) return;
    try {
      setIsApiKeyLoading(true);
      const res = await fetch(`${serverUrl}/api/api-key/regenerate`, {
        method: "PUT",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to regenerate API key");
      const data = await res.json();
      const keyString = extractKeyString(data);
      setApiKey(keyString);
      setShowApiKey(false);
      setIsApiKeyModalOpen(true);
      setApiKeyExists(true);
    } catch (err) {
      swalError(err.message || "Failed to regenerate API key");
    } finally {
      setIsApiKeyLoading(false);
    }
  };

  const handleCopyApiKey = () => {
    if (typeof apiKey === "string" && apiKey.length > 0) {
      navigator.clipboard.writeText(apiKey);
      swalSuccess("API Key copied to clipboard!");
    } else {
      swalError("No API Key to copy!");
    }
  };

  // Refresh Google Classroom data API
  const refreshGoogleData = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch(
        `${serverUrl}/api/google-classroom/sync-all`,
        { credentials: "include" },
      );
      setIsLoading(false);
      if (!response.ok) {
        throw new Error("Failed to sync Google Classroom data");
      }
      swalSuccess("Google Classroom data refreshed successfully.");

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      swalError(error.message || "Failed to sync Google Classroom data");
    }
  }, []);

  // Refresh Canvas data API
  const refreshCanvasData = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${serverUrl}/api/canvas/sync-all`, {
        credentials: "include",
      });
      setIsLoading(false);
      if (!response.ok) {
        throw new Error("Failed to sync Canvas data");
      }
      swalSuccess("Canvas data refreshed successfully.");

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      swalError(error.message || "Failed to sync Canvas data");
    }
  }, []);

  // Source system and refresh method mapping
  const refreshDataSourceSystemConfig = {
    "Google Classroom": refreshGoogleData,
    Canvas: refreshCanvasData,
  };

  const handleRefresh = async () => {
    const refreshFn = refreshDataSourceSystemConfig[user.sourceSystem];
    if (!refreshFn) return;
    refreshFn();
  };

  // Handle Sign Out with Confirmation
  const handleSignOut = async () => {
    try {
      const response = await fetch(`${serverUrl}/api/auth/signout`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to sign out");
      }

      logout();
      navigate("/login");
    } catch (error) {
      swalError(error.message || "An error occurred while signing out");
    }
  };

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Password strength checker
  const getPasswordStrength = (password) => {
    if (!password) return { score: 0, label: "", color: "" };
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (score <= 2) return { score, label: "Weak", color: "#ef4136" };
    if (score <= 3) return { score, label: "Fair", color: "#f09a21" };
    if (score <= 4) return { score, label: "Good", color: "#8dc63f" };
    return { score, label: "Strong", color: "#22c55e" };
  };

  const passwordStrength = getPasswordStrength(passwordData.newPassword);

  const validatePasswordForm = () => {
    const newErrors = {};
    if (!passwordData.currentPassword.trim())
      newErrors.currentPassword = "Current password is required";
    if (!passwordData.newPassword.trim())
      newErrors.newPassword = "New password is required";
    else if (passwordData.newPassword.length < 8)
      newErrors.newPassword = "Password must be at least 8 characters";
    else if (!/[A-Z]/.test(passwordData.newPassword))
      newErrors.newPassword = "Must include at least one uppercase letter";
    else if (!/[a-z]/.test(passwordData.newPassword))
      newErrors.newPassword = "Must include at least one lowercase letter";
    else if (!/[0-9]/.test(passwordData.newPassword))
      newErrors.newPassword = "Must include at least one number";
    else if (!/[^A-Za-z0-9]/.test(passwordData.newPassword))
      newErrors.newPassword = "Must include at least one special character";
    if (!passwordData.confirmNewPassword.trim())
      newErrors.confirmNewPassword = "Please confirm your new password";
    else if (passwordData.newPassword !== passwordData.confirmNewPassword)
      newErrors.confirmNewPassword = "Passwords do not match";
    if (
      passwordData.currentPassword &&
      passwordData.newPassword &&
      passwordData.currentPassword === passwordData.newPassword
    )
      newErrors.newPassword = "New password must differ from current password";
    setPasswordErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePasswordModalOpen = () => {
    setPasswordData({
      currentPassword: "",
      newPassword: "",
      confirmNewPassword: "",
    });
    setPasswordErrors({});
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setIsDropdownOpen(false);
    setIsPasswordModalOpen(true);
  };

  const handlePasswordModalClose = () => {
    if (isChangingPassword) return;
    setIsPasswordModalOpen(false);
    setPasswordData({
      currentPassword: "",
      newPassword: "",
      confirmNewPassword: "",
    });
    setPasswordErrors({});
  };

  const handlePasswordInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
    if (passwordErrors[name]) {
      setPasswordErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!validatePasswordForm()) return;

    setIsChangingPassword(true);
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/user/change-password`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: user.email,
            currentPassword: passwordData.currentPassword,
            newPassword: passwordData.newPassword,
          }),
        },
      );
      if (!response) return;
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to change password");
      }
      setIsPasswordModalOpen(false);
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmNewPassword: "",
      });
      swalSuccess("Password changed successfully!");
    } catch (err) {
      swalError(err.message || "Failed to change password.");
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Render navigation link based on type
  const renderLink = (link) => {
    if (link.component) {
      return React.cloneElement(link.component, {
        className:
          `nav-link ${link.className || ""} ${location.pathname === link.to ? "active" : ""}`.trim(),
        onClick: () => {
          link.onClick?.();
          setIsMenuOpen(false);
        },
        key: link.id || link.text,
      });
    }

    const badgeValue =
      link.badgeCount !== undefined ? link.badgeCount : link.badge;
    const hasBadge =
      badgeValue !== undefined &&
      badgeValue !== null &&
      (Number(badgeValue) > 0 ||
        (link.showZeroBadge && Number(badgeValue) === 0));

    if (link.to) {
      return (
        <NavLink
          to={link.locked ? "#" : link.to}
          end={link.end}
          className={({ isActive }) =>
            `nav-link ${link.className || ""} ${isActive && !link.locked ? "active" : ""} ${link.locked ? "nav-link-locked" : ""}`.trim()
          }
          onClick={(e) => {
            if (link.locked) {
              e.preventDefault();
              e.stopPropagation();
              link.onClick?.(e);
              setIsMenuOpen(false);
              return;
            }
            link.onClick?.();
            setIsMenuOpen(false);
          }}
          key={link.id}
          title={link.locked ? "Locked: Click to enable this feature" : undefined}
        >
          <span>{link.text}</span>
          {link.locked && (
            <i
              className="fa-solid fa-lock"
              style={{
                marginLeft: "6px",
                fontSize: "0.72rem",
                color: "#94a3b8",
              }}
            />
          )}
          {hasBadge && (
            <span
              className={`nav-link-badge ${link.badgeClassName || ""}`}
              title={link.badgeTitle || `${badgeValue} Pending`}
            >
              {badgeValue}
            </span>
          )}
        </NavLink>
      );
    }

    return (
      <a
        href={link.href || "#"}
        className={`nav-link ${link.className || ""} ${
          location.pathname === link.href ? "active" : ""
        }`.trim()}
        onClick={(e) => {
          if (!link.href) e.preventDefault();
          link.onClick?.();
          setIsMenuOpen(false);
        }}
        key={link.id || link.text}
      >
        <span>{link.text}</span>
        {hasBadge && (
          <span
            className={`nav-link-badge ${link.badgeClassName || ""}`}
            title={link.badgeTitle || `${badgeValue} Pending`}
          >
            {badgeValue}
          </span>
        )}
      </a>
    );
  };

  // Derive notification status label and color for the dropdown toggle
  const notifStatus = !pushSupported
    ? { label: "Unsupported", color: "#94a3b8" }
    : pushPermission === "denied"
      ? { label: "Blocked", color: "#ef4444" }
      : pushSubscribed
        ? { label: "On", color: "#22c55e" }
        : { label: "Off", color: "#94a3b8" };

  return (
    <>
      <MaintenanceBanner />
      <PWAInstallBanner />
      <motion.header
        className={`navbar ${className}`}
        variants={navbarVariants}
      >
        <div className="navbar-brand">
          <motion.h1 whileTap={{ scale: 0.95 }}>
            <img src="/imgs/logo.svg" alt="grademe-logo" width={200} />
          </motion.h1>

          <div className="breadcrumb-container desktop-breadcrumbs">
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={crumb.id || index}>
                {index > 0 && (
                  <motion.span
                    className="breadcrumb-separator"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <i className="fas fa-chevron-right" />
                  </motion.span>
                )}
                <motion.span
                  className={`breadcrumb-item ${crumb.isLast ? "active" : ""}`}
                  onClick={() => {
                    if (crumb.isLast) return;
                    if (crumb.onClick) crumb.onClick();
                    else navigate(crumb.path);
                  }}
                  whileHover={!crumb.isLast ? { scale: 1.05 } : {}}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  title={crumb.name}
                >
                  {crumb.name}
                </motion.span>
              </React.Fragment>
            ))}
          </div>

          {breadcrumbs.length > 0 && (
            <>
              <motion.button
                className="breadcrumbs-toggle mobile-breadcrumbs"
                onClick={handleBreadcrumbsToggle}
                whileTap={{ scale: 0.95 }}
                aria-label="Toggle breadcrumbs"
              >
                <AnimatePresence mode="wait">
                  <motion.i
                    key={isBreadcrumbsOpen ? "close" : "menu"}
                    initial={{ rotate: -180, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 180, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="fas fa-chevron-down"
                  />
                </AnimatePresence>
              </motion.button>

              <motion.div
                className={`breadcrumb-container mobile-breadcrumbs ${
                  isBreadcrumbsOpen ? "show" : "hide"
                }`}
                initial={false}
                animate={{
                  height: isBreadcrumbsOpen ? "auto" : 0,
                  opacity: isBreadcrumbsOpen ? 1 : 0,
                }}
                transition={{ duration: 0.3 }}
              >
                {breadcrumbs.map((crumb, index) => (
                  <React.Fragment key={crumb.id || index}>
                    {index > 0 && (
                      <motion.span
                        className="breadcrumb-separator"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <i className="fas fa-chevron-right" />
                      </motion.span>
                    )}
                    <motion.span
                      className={`breadcrumb-item ${
                        crumb.isLast ? "active" : ""
                      }`}
                      onClick={() => {
                        if (crumb.isLast) return;
                        if (crumb.onClick) crumb.onClick();
                        else
                          navigate(
                            crumb.path,
                            crumb.state ? { state: crumb.state } : undefined,
                          );
                      }}
                      whileHover={!crumb.isLast ? { scale: 1.05 } : {}}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      {crumb.name}
                    </motion.span>
                  </React.Fragment>
                ))}
              </motion.div>
            </>
          )}
        </div>

        {links?.length > 0 && (
          <nav className={`navbar-links ${isMenuOpen ? "show" : "hide"}`}>
            {links.filter(Boolean).map((link) => renderLink(link))}
          </nav>
        )}
        <div className="navbar-actions">
          {/* Render multiple create buttons */}
          {createButtons &&
            createButtons.length > 0 &&
            createButtons.map((button, index) => (
              <button
                key={index}
                className={`create-btn ${buttonClassName}`}
                onClick={button.onClick}
              >
                {button.text}
                <i className="fas fa-plus"></i>
              </button>
            ))}
          {user?.sourceSystem && user?.role[0] !== "student" && (
            <button
              className="sync-platform-btn"
              onClick={handleRefresh}
              title={`Sync ${
                user.sourceSystem === "Canvas"
                  ? "Canvas LMS"
                  : "Google Classroom"
              } Data`}
              disabled={isLoading}
            >
              <div className="sync-icon-wrapper">
                <img
                  src={
                    user.sourceSystem === "Canvas"
                      ? "/imgs/canvas_logo.png"
                      : "/imgs/google_classroom_logo.png"
                  }
                  alt={`${user.sourceSystem} logo`}
                  className={`platform-logo ${isLoading ? "syncing" : ""}`}
                />
                {isLoading ? (
                  <div className="sync-spinner">
                    <i className="fa-solid fa-circle-notch fa-spin"></i>
                  </div>
                ) : (
                  <div className="sync-icon">
                    <i className="fa-solid fa-arrows-rotate"></i>
                  </div>
                )}
              </div>
              <span className="sync-text">
                {isLoading ? "Syncing..." : "Sync Data"}
              </span>
            </button>
          )}

          {/* UDGHOSH Renewal Glowing Button */}
          {isUdghoshStudent && user?.loginExpiryDate && (
            <motion.button
              className="pp-navbar-renew-btn"
              onClick={() => setIsPaymentModalOpen(true)}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              type="button"
              title="Renew / Extend Your GradeMe AI Access"
            >
              <i className="fa-solid fa-bolt"></i>
              <span>
                {udghoshExpiryInfo &&
                udghoshExpiryInfo.daysRemaining > 0 &&
                udghoshExpiryInfo.daysRemaining <= 30
                  ? `${udghoshExpiryInfo.daysRemaining}d Left • Renew`
                  : "Renew Access"}
              </span>
              <span className="pp-navbar-renew-badge">Save 50%</span>
            </motion.button>
          )}

          {/* Conditionally render the profile button container */}
          {!isProfileHidden && (
            <div className="profile-btn-container" ref={dropdownRef}>
              <motion.button
                className="profile-btn"
                onClick={handleProfileClick}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <motion.img
                  src="/imgs/person_icon.svg"
                  alt="profile"
                  width={50}
                  initial={{ rotate: 0 }}
                  animate={{ rotate: isDropdownOpen ? 360 : 0 }}
                  transition={{ duration: 0.5 }}
                />
              </motion.button>

              <AnimatePresence>
                {isDropdownOpen && (
                  <motion.div
                    className="profile-dropdown"
                    initial={{ opacity: 0, y: -20, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -20, scale: 0.95 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                  >
                    <motion.div
                      className="profile-info"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 }}
                    >
                      <div
                        className="profile-header"
                        onClick={handleViewProfile}
                        title="View Profile"
                      >
                        <motion.div
                          className="profile-avatar"
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: 0.2 }}
                        >
                          <img src="/imgs/person_icon.svg" alt="profile" />
                        </motion.div>
                        <motion.div
                          className="profile-name"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.3 }}
                        >
                          <h3>{`${user?.firstName || ""} ${
                            user?.lastName || ""
                          }`}</h3>
                          <span>
                            {primaryRole?.charAt(0).toUpperCase() +
                              primaryRole?.slice(1)}
                          </span>
                        </motion.div>
                      </div>

                      {primaryRole !== "student" && (
                        <motion.div
                          className="profile-details"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.4 }}
                        >
                          {isApiKeyLoading ? (
                            <Loader fontSize="5px" style={{ left: "0" }} />
                          ) : apiKeyExists ? (
                            <button
                              className="profile-action-btn"
                              style={{
                                background: "#ffde17",
                                color: "#000",
                              }}
                              onClick={handleRegenerateApiKey}
                              type="button"
                            >
                              <i className="fa-solid fa-arrows-rotate"></i>{" "}
                              Regenerate API Key
                            </button>
                          ) : (
                            <button
                              className="profile-action-btn"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: 0.4 }}
                              style={{
                                background: "#ffde17",
                                color: "#000",
                              }}
                              onClick={handleGenerateApiKey}
                              type="button"
                            >
                              <i className="fa-solid fa-key"></i> Generate API
                              Key
                            </button>
                          )}
                        </motion.div>
                      )}

                      {/* UDGHOSH Student Subscription Validity & Action */}
                      {isUdghoshStudent && user?.loginExpiryDate && (
                        <motion.div
                          className="profile-details"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.4 }}
                          style={{
                            borderTop: "1px solid #f1f5f9",
                            paddingTop: "0.55rem",
                            marginTop: "0.5rem",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "0.75rem",
                              color: "#64748b",
                              marginBottom: "0.4rem",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <span>Access Status:</span>
                            <strong
                              style={{
                                color: udghoshExpiryInfo?.isExpired
                                  ? "#ef4444"
                                  : "#0f172a",
                              }}
                            >
                              {udghoshExpiryInfo?.formattedDate
                                ? udghoshExpiryInfo.isExpired
                                  ? "Expired"
                                  : `Valid till ${udghoshExpiryInfo.formattedDate}`
                                : "Active"}
                            </strong>
                          </div>
                          <button
                            className="profile-action-btn"
                            style={{
                              background:
                                "linear-gradient(135deg, #f09a21, #da1c5c)",
                              color: "#ffffff",
                              fontWeight: "700",
                              border: "none",
                            }}
                            onClick={() => {
                              setIsDropdownOpen(false);
                              setIsPaymentModalOpen(true);
                            }}
                            type="button"
                          >
                            <i className="fa-solid fa-bolt"></i> Renew Plan
                            (₹399)
                          </button>
                        </motion.div>
                      )}
                    </motion.div>

                    {/* Notification Toggle */}
                    <motion.div
                      className="notification-toggle-row"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.35 }}
                      onClick={handleNotificationClick}
                      role="button"
                      tabIndex={0}
                      aria-label={
                        pushSubscribed
                          ? "Disable notifications"
                          : "Enable notifications"
                      }
                    >
                      <div className="notification-toggle-left">
                        <i
                          className={`fa-solid ${
                            !pushSupported || pushPermission === "denied"
                              ? "fa-bell-slash"
                              : "fa-bell"
                          }`}
                          style={{ color: notifStatus.color }}
                        />
                        <div className="notification-toggle-label">
                          <span className="notification-toggle-title">
                            Notifications
                          </span>
                          <span
                            className="notification-toggle-status"
                            style={{ color: notifStatus.color }}
                          >
                            {pushLoading
                              ? "Checking…"
                              : pushActionLoading
                                ? "Updating…"
                                : notifStatus.label}
                          </span>
                        </div>
                      </div>

                      <div
                        className={`notification-toggle-switch ${
                          pushSubscribed ? "on" : ""
                        } ${
                          !pushSupported || pushPermission === "denied"
                            ? "blocked"
                            : ""
                        } ${pushLoading || pushActionLoading ? "loading" : ""}`}
                      >
                        <div className="notification-toggle-knob">
                          {(pushLoading || pushActionLoading) && (
                            <i className="fa-solid fa-circle-notch fa-spin notification-toggle-spinner" />
                          )}
                        </div>
                      </div>
                    </motion.div>

                    {/* Role Switcher */}
                    {user?.role &&
                      user.role.length > 1 &&
                      user.role
                        .filter((role) => role !== primaryRole)
                        .map((role) => (
                          <motion.button
                            key={role}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.4 }}
                            className="profile-action-btn"
                            onClick={() => switchRole(role)}
                            style={{
                              background: "#ffde17",
                              color: "#000",
                            }}
                          >
                            <i className="fa-solid fa-repeat"></i>
                            <span>
                              Switch to{" "}
                              {role.charAt(0).toUpperCase() + role.slice(1)}
                            </span>
                          </motion.button>
                        ))}

                    {/* Change Password */}
                    <motion.button
                      type="button"
                      className="profile-action-btn"
                      onClick={handlePasswordModalOpen}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.4 }}
                      style={{
                        background: "var(--color-1)",
                        color: "#fff",
                      }}
                    >
                      <i className="fa-solid fa-lock"></i>
                      <span>Change Password</span>
                    </motion.button>

                    <motion.button
                      className="profile-action-btn"
                      onClick={handleSignOut}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.4 }}
                    >
                      <i className="fa-solid fa-right-from-bracket"></i>
                      <span>Sign Out</span>
                    </motion.button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>
      </motion.header>
      <Modal
        isOpen={isApiKeyModalOpen}
        onClose={() => setIsApiKeyModalOpen(false)}
      >
        <h2>Your API Key</h2>
        <div style={{ margin: "1rem 0" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              background: "#f3f4f6",
              padding: "0.8rem 1rem",
              borderRadius: "8px",
              fontFamily: "monospace",
              fontSize: "1.1rem",
              wordBreak: "break-all",
              gap: 0,
            }}
          >
            <span>
              {showApiKey && typeof apiKey === "string"
                ? apiKey
                : "•".repeat(typeof apiKey === "string" ? apiKey.length : 16)}
            </span>
            <div style={{ flex: 1 }} />
            <button
              onClick={() => setShowApiKey((v) => !v)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "1.1rem",
                marginRight: "1rem",
              }}
              aria-label={showApiKey ? "Hide API Key" : "Show API Key"}
              title={showApiKey ? "Hide API Key" : "Show API Key"}
            >
              <i className={`fa-solid fa-eye${showApiKey ? "-slash" : ""}`}></i>
            </button>
            <button
              onClick={handleCopyApiKey}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "1.1rem",
                marginLeft: "0.3rem",
              }}
              aria-label="Copy API Key"
              title="Copy API Key"
            >
              <i className="fa-solid fa-copy"></i>
            </button>
          </div>
          <div style={{ color: "#b91c1c", marginTop: "1rem", fontWeight: 500 }}>
            <i className="fa-solid fa-triangle-exclamation"></i> <b>Note:</b>{" "}
            This key will only be shown once. If you lose it, you must
            regenerate a new key. Store it securely.
          </div>
        </div>
      </Modal>

      {/* Change Password Modal */}
      <Modal isOpen={isPasswordModalOpen} onClose={handlePasswordModalClose}>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          <div className="cp-modal-header">
            <div className="cp-modal-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <rect
                  x="3"
                  y="11"
                  width="18"
                  height="11"
                  rx="2"
                  ry="2"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M7 11V7a5 5 0 0 1 10 0v4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle cx="12" cy="16" r="1" fill="currentColor" />
              </svg>
            </div>
            <h2>Change Password</h2>
            <p className="modal-description">
              Keep your account secure by updating your password regularly.
            </p>
          </div>

          <form onSubmit={handleChangePassword} className="user-form cp-form">
            {/* Current Password */}
            <div className="form-group">
              <label>
                <i className="fa-solid fa-asterisk requiredIcon"></i> Current
                Password
              </label>
              <div className="cp-input-wrapper">
                <input
                  type={showCurrentPassword ? "text" : "password"}
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  placeholder="Enter your current password"
                  onChange={handlePasswordInputChange}
                  required
                  disabled={isChangingPassword}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="cp-toggle-visibility"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  tabIndex={-1}
                >
                  {showCurrentPassword ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <line
                        x1="1"
                        y1="1"
                        x2="23"
                        y2="23"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle
                        cx="12"
                        cy="12"
                        r="3"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>
              </div>
              {passwordErrors.currentPassword && (
                <motion.span
                  className="error"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                >
                  {passwordErrors.currentPassword}
                </motion.span>
              )}
            </div>

            {/* New Password */}
            <div className="form-group">
              <label>
                <i className="fa-solid fa-asterisk requiredIcon"></i> New
                Password
              </label>
              <div className="cp-input-wrapper">
                <input
                  type={showNewPassword ? "text" : "password"}
                  name="newPassword"
                  value={passwordData.newPassword}
                  placeholder="Enter a strong new password"
                  onChange={handlePasswordInputChange}
                  required
                  disabled={isChangingPassword}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="cp-toggle-visibility"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  tabIndex={-1}
                >
                  {showNewPassword ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <line
                        x1="1"
                        y1="1"
                        x2="23"
                        y2="23"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle
                        cx="12"
                        cy="12"
                        r="3"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>
              </div>
              {/* Password Strength Bar */}
              {passwordData.newPassword && (
                <motion.div
                  className="cp-strength-container"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="cp-strength-bar-track">
                    <motion.div
                      className="cp-strength-bar-fill"
                      initial={{ width: 0 }}
                      animate={{
                        width: `${(passwordStrength.score / 5) * 100}%`,
                      }}
                      style={{ backgroundColor: passwordStrength.color }}
                      transition={{ duration: 0.4, ease: "easeOut" }}
                    />
                  </div>
                  <span
                    className="cp-strength-label"
                    style={{ color: passwordStrength.color }}
                  >
                    {passwordStrength.label}
                  </span>
                </motion.div>
              )}
              {/* Password Requirements */}
              {passwordData.newPassword && (
                <motion.div
                  className="cp-requirements"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.1 }}
                >
                  <div
                    className={`cp-req-item ${passwordData.newPassword.length >= 8 ? "met" : ""}`}
                  >
                    <span className="cp-req-icon">
                      {passwordData.newPassword.length >= 8 ? "✓" : "○"}
                    </span>
                    <span>At least 8 characters</span>
                  </div>
                  <div
                    className={`cp-req-item ${/[A-Z]/.test(passwordData.newPassword) ? "met" : ""}`}
                  >
                    <span className="cp-req-icon">
                      {/[A-Z]/.test(passwordData.newPassword) ? "✓" : "○"}
                    </span>
                    <span>One uppercase letter</span>
                  </div>
                  <div
                    className={`cp-req-item ${/[a-z]/.test(passwordData.newPassword) ? "met" : ""}`}
                  >
                    <span className="cp-req-icon">
                      {/[a-z]/.test(passwordData.newPassword) ? "✓" : "○"}
                    </span>
                    <span>One lowercase letter</span>
                  </div>
                  <div
                    className={`cp-req-item ${/[0-9]/.test(passwordData.newPassword) ? "met" : ""}`}
                  >
                    <span className="cp-req-icon">
                      {/[0-9]/.test(passwordData.newPassword) ? "✓" : "○"}
                    </span>
                    <span>One number</span>
                  </div>
                  <div
                    className={`cp-req-item ${/[^A-Za-z0-9]/.test(passwordData.newPassword) ? "met" : ""}`}
                  >
                    <span className="cp-req-icon">
                      {/[^A-Za-z0-9]/.test(passwordData.newPassword)
                        ? "✓"
                        : "○"}
                    </span>
                    <span>One special character</span>
                  </div>
                </motion.div>
              )}
              {passwordErrors.newPassword && (
                <motion.span
                  className="error"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                >
                  {passwordErrors.newPassword}
                </motion.span>
              )}
            </div>

            {/* Confirm New Password */}
            <div className="form-group">
              <label>
                <i className="fa-solid fa-asterisk requiredIcon"></i> Confirm
                New Password
              </label>
              <div className="cp-input-wrapper">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmNewPassword"
                  value={passwordData.confirmNewPassword}
                  placeholder="Re-enter your new password"
                  onChange={handlePasswordInputChange}
                  required
                  disabled={isChangingPassword}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="cp-toggle-visibility"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <line
                        x1="1"
                        y1="1"
                        x2="23"
                        y2="23"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle
                        cx="12"
                        cy="12"
                        r="3"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>
              </div>
              {/* Match indicator */}
              {passwordData.confirmNewPassword && passwordData.newPassword && (
                <motion.div
                  className={`cp-match-indicator ${passwordData.newPassword === passwordData.confirmNewPassword ? "match" : "no-match"}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <span>
                    {passwordData.newPassword ===
                    passwordData.confirmNewPassword
                      ? "✓ Passwords match"
                      : "✗ Passwords do not match"}
                  </span>
                </motion.div>
              )}
              {passwordErrors.confirmNewPassword && (
                <motion.span
                  className="error"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                >
                  {passwordErrors.confirmNewPassword}
                </motion.span>
              )}
            </div>

            {isChangingPassword ? (
              <Loader />
            ) : (
              <motion.button
                type="submit"
                className="btn-submit cp-submit-btn"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  style={{ marginRight: "8px" }}
                >
                  <rect
                    x="3"
                    y="11"
                    width="18"
                    height="11"
                    rx="2"
                    ry="2"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M7 11V7a5 5 0 0 1 10 0v4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Update Password
              </motion.button>
            )}
          </form>
        </motion.div>
      </Modal>

      {/* Bottom Tabbar - Mobile App Style Navigation (Mobile Only) */}
      {links && links.length > 0 && (
        <motion.nav
          className="bottom-tabbar"
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          {links.filter(Boolean).map((link) => {
            const isActive =
              !link.locked &&
              (link.end
                ? location.pathname === link.to
                : location.pathname.startsWith(link.to));
            return (
              <NavLink
                key={link.id}
                to={link.locked ? "#" : link.to}
                end={link.end}
                className={`tabbar-item ${isActive ? "active" : ""} ${link.className || ""} ${link.locked ? "tabbar-item-locked" : ""}`.trim()}
                onClick={(e) => {
                  if (link.locked) {
                    e.preventDefault();
                    e.stopPropagation();
                    link.onClick?.(e);
                  }
                }}
              >
                <motion.div
                  className="tabbar-icon-wrapper"
                  whileTap={{ scale: 0.9 }}
                >
                  <i className={`${link.icon}`}></i>
                  {link.locked && (
                    <i
                      className="fa-solid fa-lock"
                      style={{
                        position: "absolute",
                        top: "-2px",
                        right: "-4px",
                        fontSize: "0.6rem",
                        color: "var(--color-2)",
                      }}
                    />
                  )}
                  {isActive && (
                    <motion.div
                      className="tabbar-active-indicator"
                      layoutId="activeTab"
                      initial={false}
                      transition={{
                        type: "spring",
                        stiffness: 500,
                        damping: 30,
                      }}
                    />
                  )}
                </motion.div>
                <span className="tabbar-label">
                  {link.text}
                  {link.locked && (
                    <i
                      className="fa-solid fa-lock"
                      style={{ fontSize: "0.6rem", marginLeft: "3px" }}
                    />
                  )}
                </span>
              </NavLink>
            );
          })}
        </motion.nav>
      )}

      {pushMessage && (
        <div
          className="push-message-overlay"
          onClick={() => setPushMessage(null)}
        >
          <motion.div
            className="push-message-modal"
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="push-message-header">
              <div className={`push-message-icon ${pushMessage.type}`}>
                <i
                  className={`fa-solid ${
                    pushMessage.type === "blocked" ? "fa-bell-slash" : "fa-bell"
                  }`}
                />
              </div>

              <div className="push-message-heading">
                <h3>{pushMessage.title}</h3>
                <p>{pushMessage.message}</p>
              </div>

              <button
                type="button"
                className="push-message-close"
                onClick={() => setPushMessage(null)}
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>

            {pushMessage.type === "blocked" && (
              <div className="push-message-content">
                <p className="push-message-section-title">
                  Step 1: Enable in site settings
                </p>

                <ol>
                  <li>
                    Tap the <strong>lock / tune icon</strong> next to the
                    website address.
                  </li>

                  <li>
                    Find <strong>Notifications</strong>.
                  </li>

                  <li>
                    Change the setting to <strong>Allow</strong>.
                  </li>

                  <li>Return here and tap the notification toggle again.</li>
                </ol>

                <div className="push-message-note">
                  <i className="fa-solid fa-circle-info" />

                  <span>
                    Browsers do not show the permission popup again while
                    notifications are blocked in site settings.
                  </span>
                </div>

                <p
                  className="push-message-section-title"
                  style={{ marginTop: "16px" }}
                >
                  Still not seeing the option?
                </p>
                <p
                  style={{
                    fontSize: "13px",
                    lineHeight: "1.5",
                    margin: "0 0 8px",
                    opacity: 0.8,
                  }}
                >
                  Your browser's app-level notifications may be disabled on your
                  device.
                </p>
                <ol>
                  <li>
                    Open your phone's <strong>Settings</strong>
                  </li>
                  <li>
                    Go to <strong>Apps</strong> → <strong>Chrome</strong> (or
                    your browser)
                  </li>
                  <li>
                    Tap <strong>Notifications</strong> and make sure they are{" "}
                    <strong>enabled</strong>
                  </li>
                  <li>Come back and try again</li>
                </ol>
              </div>
            )}

            {pushMessage.type === "unsupported" && (
              <div className="push-message-content">
                <div className="push-message-note">
                  <i className="fa-solid fa-circle-info" />

                  <span>
                    Try using a recent version of Chrome, Edge, Firefox, or
                    Safari if you want to receive push notifications.
                  </span>
                </div>
              </div>
            )}

            <div className="push-message-footer">
              <button
                type="button"
                className="push-message-ok"
                onClick={() => setPushMessage(null)}
              >
                Got it
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* In-App Renewal Payment Modal */}
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
      />
    </>
  );
}

Navbar.propTypes = {
  links: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string,
        to: PropTypes.string,
        href: PropTypes.string,
        onClick: PropTypes.func,
        component: PropTypes.element,
        end: PropTypes.bool,
        id: PropTypes.string,
        icon: PropTypes.string, // FontAwesome icon class (e.g., "fa-home")
      }),
    ),
    PropTypes.oneOf([null, undefined]),
  ]),
  createButtons: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
    }),
  ),
  className: PropTypes.string,
  buttonClassName: PropTypes.string,
  customLinkComponent: PropTypes.elementType,
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      path: PropTypes.string.isRequired,
      isLast: PropTypes.bool,
      id: PropTypes.string,
      onClick: PropTypes.func,
    }),
  ),
  isProfileHidden: PropTypes.bool,
};
